import Cookies from "js-cookie";
import { cookieConsentService, CookieCategory } from "./cookieConsentService";

interface CookieOptions {
  expires?: number | Date; // מספר ימים או תאריך ספציפי
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
}

interface UserPreferences {
  language: string;
  theme: "light" | "dark";
  notifications: boolean;
}

interface ShoppingCart {
  items: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
  }>;
  total: number;
}

class CookieService {
  private readonly DEFAULT_OPTIONS: CookieOptions = {
    expires: 30, // 30 days default
    path: "/",
    secure: process.env.NODE_ENV === "production",
    sameSite: "lax",
  };

  /**
   * שמירת עוגיה עם בדיקת הסכמה
   */
  set<T>(name: string, value: T, options?: CookieOptions): void {
    try {
      // בדיקה אם יש הרשאה לשמור את הקוקיז
      if (!cookieConsentService.hasConsentForCookie(name)) {
                return;
      }

      const cookieValue =
        typeof value === "string" ? value : JSON.stringify(value);
      const cookieOptions = { ...this.DEFAULT_OPTIONS, ...options };

      Cookies.set(name, cookieValue, cookieOptions);
    } catch (error) {
          }
  }

  /**
   * שליפת עוגיה
   */
  get<T>(name: string): T | null {
    try {
      const value = Cookies.get(name);
      if (value === undefined) {
        return null;
      }

      // ניסיון לפרסר JSON
      try {
        const parsed = JSON.parse(value);
        return parsed as T;
      } catch {
        // אם לא JSON, החזרת הערך כפי שהוא
        return value as T;
      }
    } catch (error) {
            return null;
    }
  }

  /**
   * בדיקה אם עוגיה קיימת
   */
  has(name: string): boolean {
    return Cookies.get(name) !== undefined;
  }

  /**
   * מחיקת עוגיה
   */
  remove(name: string, options?: CookieOptions): void {
    try {
      const cookieOptions = { ...this.DEFAULT_OPTIONS, ...options };
      Cookies.remove(name, cookieOptions);
    } catch (error) {
          }
  }

  /**
   * קבלת כל שמות העוגיות
   */
  getAll(): Record<string, string> {
    return Cookies.get();
  }

  /**
   * ניקוי כל העוגיות
   */
  clear(): void {
    try {
      const allCookies = this.getAll();
      Object.keys(allCookies).forEach((name) => {
        this.remove(name);
      });
    } catch (error) {
          }
  }

  // ===== פונקציות מיוחדות =====

  /**
   * שמירת העדפות משתמש
   */
  setUserPreferences(preferences: UserPreferences): void {
    this.set("user_preferences", preferences, { expires: 365 }); // שנה
  }

  /**
   * שליפת העדפות משתמש
   */
  getUserPreferences(): UserPreferences | null {
    return this.get<UserPreferences>("user_preferences");
  }

  /**
   * שמירת עגלת קניות
   */
  setShoppingCart(cart: ShoppingCart): void {
    this.set("shopping_cart", cart, { expires: 7 }); // שבוע
  }

  /**
   * שליפת עגלת קניות
   */
  getShoppingCart(): ShoppingCart | null {
    return this.get<ShoppingCart>("shopping_cart");
  }

  /**
   * שמירת שפה מועדפת
   */
  setLanguage(language: string): void {
    this.set("preferred_language", language, { expires: 365 });
  }

  /**
   * שליפת שפה מועדפת
   */
  getLanguage(): string | null {
    return this.get<string>("preferred_language");
  }

  /**
   * שמירת ערכת נושא
   */
  setTheme(theme: "light" | "dark"): void {
    this.set("theme", theme, { expires: 365 });
  }

  /**
   * שליפת ערכת נושא
   */
  getTheme(): "light" | "dark" | null {
    return this.get<"light" | "dark">("theme");
  }

  /**
   * שמירת טוקן אימות
   */
  setAuthToken(token: string): void {
    this.set("auth_token", token, {
      expires: 1, // יום אחד
      secure: true,
      sameSite: "strict",
    });
  }

  /**
   * שליפת טוקן אימות
   */
  getAuthToken(): string | null {
    return this.get<string>("auth_token");
  }

  /**
   * מחיקת טוקן אימות
   */
  removeAuthToken(): void {
    this.remove("auth_token");
  }

  /**
   * שמירת היסטוריית חיפוש
   */
  setSearchHistory(searches: string[]): void {
    this.set("search_history", searches, { expires: 30 });
  }

  /**
   * שליפת היסטוריית חיפוש
   */
  getSearchHistory(): string[] | null {
    return this.get<string[]>("search_history") || [];
  }

  /**
   * הוספת חיפוש להיסטוריה
   */
  addToSearchHistory(search: string): void {
    const history = this.getSearchHistory() || [];
    const newHistory = [search, ...history.filter((s) => s !== search)].slice(
      0,
      10
    ); // מקסימום 10 חיפושים
    this.setSearchHistory(newHistory);
  }

  /**
   * שמירת העדפות התראות
   */
  setNotificationPreferences(enabled: boolean): void {
    this.set("notifications_enabled", enabled, { expires: 365 });
  }

  /**
   * שליפת העדפות התראות
   */
  getNotificationPreferences(): boolean {
    return this.get<boolean>("notifications_enabled") ?? true; // ברירת מחדל: מופעל
  }

  /**
   * שמירת זמן ביקור אחרון
   */
  setLastVisit(): void {
    this.set("last_visit", new Date().toISOString(), { expires: 365 });
  }

  /**
   * שליפת זמן ביקור אחרון
   */
  getLastVisit(): Date | null {
    const lastVisit = this.get<string>("last_visit");
    return lastVisit ? new Date(lastVisit) : null;
  }

  /**
   * בדיקה אם המשתמש מבקר בפעם הראשונה
   */
  isFirstVisit(): boolean {
    return !this.has("last_visit");
  }

  /**
   * שמירת העדפות נגישות
   */
  setAccessibilityPreferences(preferences: {
    fontSize: "small" | "medium" | "large";
    highContrast: boolean;
    reduceMotion: boolean;
  }): void {
    this.set("accessibility_preferences", preferences, { expires: 365 });
  }

  /**
   * שליפת העדפות נגישות
   */
  getAccessibilityPreferences(): {
    fontSize: "small" | "medium" | "large";
    highContrast: boolean;
    reduceMotion: boolean;
  } | null {
    return this.get("accessibility_preferences");
  }
}

// יצירת מופע גלובלי של שירות העוגיות
export const cookieService = new CookieService();

export default CookieService;
